import {createSelector} from '@reduxjs/toolkit';

import {BADGE_STATUSES} from '@/consts/loyalty/badgeStatuses';
import type {RootState} from '@/state/store';
import type {Badge, BadgeAction, Journey, JourneyLevel} from '@/types/loyalty';
import {
    getBadgeImageByViewType,
    getJourneyBadgeViewType,
    getJourneyInfo,
    isJourneyCompleted,
    isJourneyInProgress,
} from '@/utils/journeysUtils';

const selectBadgesData = (state: RootState) => state.loyaltySlice.badgesData;

const selectActiveJourneys = createSelector(
    [selectBadgesData],
    (badgesData): Journey[] => badgesData?.data?.journeys ?? []
);

const selectMemberJourneys = createSelector(
    [selectBadgesData],
    (badgesData): Journey[] => badgesData?.data?.memberJourneys ?? []
);

const selectInProgressJourneys = createSelector([selectMemberJourneys], (memberJourneys) =>
    memberJourneys.filter(isJourneyInProgress)
);

const selectCompletedJourneys = createSelector([selectMemberJourneys], (memberJourneys) =>
    memberJourneys.filter(isJourneyCompleted)
);

const resolveBadgeStatus = (journey: Journey, currentLevel: JourneyLevel): Badge['status'] => {
    if (isJourneyCompleted(journey)) return BADGE_STATUSES.COMPLETED;
    if (isJourneyInProgress(journey)) {
        // Multi-level: in-progress as soon as the journey itself is in progress.
        if ((journey.journeyLevels?.length ?? 0) > 1) return BADGE_STATUSES.IN_PROGRESS;
        // Single-level: in-progress only if the level itself has any progress.
        const total = currentLevel.memberJourneyActions?.length ?? 0;
        const left = currentLevel.actionsLeft ?? total;
        return left < total ? BADGE_STATUSES.IN_PROGRESS : BADGE_STATUSES.LOCKED;
    }
    return BADGE_STATUSES.LOCKED;
};

const mapJourneyToBadge = (journey: Journey): Badge | null => {
    const {currentLevel, currentLevelBadge, progressCompleted, progressTotal, isSingleLevel} = getJourneyInfo(journey);
    if (!currentLevelBadge?.badgeId) return null;

    const viewType = getJourneyBadgeViewType(journey);
    const imageUrl = getBadgeImageByViewType(currentLevelBadge, viewType)?.imageURL;
    const achievedImageUrl = getBadgeImageByViewType(currentLevelBadge, 'Achieved')?.imageURL;

    const rawActions = currentLevel.memberJourneyActions ?? currentLevel.journeyActions ?? [];
    const actions: BadgeAction[] = rawActions.map((action) => ({
        id: action.actionId,
        label: action.actionLabel,
        description: action.actionDescription,
        url: action.actionURL || undefined,
        isCompleted: action.memberJourneyActionStatus === 'Completed',
    }));

    return {
        id: currentLevelBadge.badgeId,
        title: journey.journeyName ?? '',
        description: journey.journeyDescription || undefined,
        extendedDescription: currentLevelBadge.badgeExtendedDescription || undefined,
        status: resolveBadgeStatus(journey, currentLevel),
        imageUrl,
        achievedImageUrl,
        totalSteps: progressTotal > 0 ? progressTotal : undefined,
        finishedSteps: progressTotal > 0 ? Math.max(0, progressCompleted) : undefined,
        actions: actions.length > 0 ? actions : undefined,
        isSingleLevel,
    };
};

const journeysToBadges = (journeys: Journey[]): Badge[] =>
    journeys.map(mapJourneyToBadge).filter((b): b is Badge => b !== null);

/**
 * "All badges" — in-progress + completed member journeys, then active (locked)
 * journeys. Order matches legacy UWM. Multi-level journeys collapse into a
 * single badge representing the current level.
 */
export const selectAllBadges = createSelector(
    [selectInProgressJourneys, selectCompletedJourneys, selectActiveJourneys],
    (inProgress, completed, active) => journeysToBadges([...inProgress, ...completed, ...active])
);

/** In-progress tab — only member journeys with status `In Progress`. */
export const selectInProgressBadges = createSelector([selectInProgressJourneys], journeysToBadges);
